import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import {
    Video, RefreshCw, Loader2, Coins, Clock, Zap,
    CheckCircle, Save, Plus, Pencil, Wand2, AlertCircle,
    Waves, Trophy, Feather, Droplet, X, Users, Search, Trash2,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface VideoModel {
    id: string;
    name: string;
    slug: string;
    provider: string;
    quality_tier: string;
    tokens_cost: number;
    generation_time_avg_seconds: number | null;
    is_active: boolean;
    is_default: boolean;
    tier_basic: boolean;
    tier_pro: boolean;
    tier_premium: boolean;
    sort_order: number;
}

interface VideoPrompt {
    id: string;
    name: string;
    slug: string;
    category: string;
    prompt_template: string;
    description: string;
    icon: string;
    is_active: boolean;
    is_default: boolean;
    sort_order: number;
}

interface PromptFormData {
    name: string;
    slug: string;
    category: string;
    promptTemplate: string;
    description: string;
    icon: string;
}

interface AiVideoUnlimitedRow {
    user_id: string;
    email: string | null;
    display_name: string | null;
    created_at: string;
}

interface AllowlistUserSearch {
    user_id: string;
    display_name: string | null;
    email: string | null;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const TIER_COLORS: Record<string, { bg: string; text: string }> = {
    fast: { bg: 'bg-emerald-500/15', text: 'text-emerald-400' },
    standard: { bg: 'bg-blue-500/15', text: 'text-blue-400' },
    premium: { bg: 'bg-purple-500/15', text: 'text-purple-400' },
    ultra: { bg: 'bg-amber-500/15', text: 'text-amber-400' },
};

const TIER_LABELS: Record<string, string> = {
    fast: 'Rápido', standard: 'Estándar', premium: 'Premium', ultra: 'Ultra',
};

const PLAN_BADGES = [
    { key: 'tier_basic', label: 'Basic', color: 'bg-zinc-500/15 text-zinc-400' },
    { key: 'tier_pro', label: 'Pro', color: 'bg-blue-500/15 text-blue-400' },
    { key: 'tier_premium', label: 'Premium', color: 'bg-purple-500/15 text-purple-400' },
] as const;

const CATEGORY_COLORS: Record<string, string> = {
    subtle: 'bg-blue-500/20 text-blue-400',
    dramatic: 'bg-orange-500/20 text-orange-400',
    artistic: 'bg-purple-500/20 text-purple-400',
    general: 'bg-zinc-500/20 text-zinc-400',
};

const CATEGORY_LABELS: Record<string, string> = {
    subtle: 'Sutil',
    dramatic: 'Dramático',
    artistic: 'Artístico',
    general: 'General',
};

const AVAILABLE_ICONS = [
    { value: 'waves', label: 'Olas', Icon: Waves },
    { value: 'zap', label: 'Rayo', Icon: Zap },
    { value: 'trophy', label: 'Trofeo', Icon: Trophy },
    { value: 'feather', label: 'Pluma', Icon: Feather },
    { value: 'droplet', label: 'Gota', Icon: Droplet },
    { value: 'wand2', label: 'Varita', Icon: Wand2 },
];

function getIconComponent(iconName: string) {
    const found = AVAILABLE_ICONS.find(i => i.value === iconName);
    return found ? found.Icon : Wand2;
}

function generateSlug(name: string) {
    return name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

const EMPTY_FORM: PromptFormData = {
    name: '',
    slug: '',
    category: 'general',
    promptTemplate: '',
    description: '',
    icon: 'waves',
};

// ─── Component ──────────────────────────────────────────────────────────────

export function AIVideoConfigPage() {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<'models' | 'prompts' | 'allowlist'>('models');
    const [editedTokens, setEditedTokens] = useState<Record<string, number>>({});

    // Prompt dialog state
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState<VideoPrompt | null>(null);
    const [formData, setFormData] = useState<PromptFormData>(EMPTY_FORM);

    // Allowlist: usuarios con cuota ilimitada de vídeo IA
    const [allowlistUserSearch, setAllowlistUserSearch] = useState('');
    const [showAllowlistSearch, setShowAllowlistSearch] = useState(false);
    const allowlistSearchRef = useRef<HTMLDivElement>(null);

    const { data: unlimitedRows = [], isLoading: unlimitedLoading, refetch: refetchUnlimited } = useQuery({
        queryKey: ['admin', 'ai-video-unlimited'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<AiVideoUnlimitedRow[]>('admin_list_ai_video_unlimited');
            if (error) throw error;
            return normalizeRpcResponse(data) as AiVideoUnlimitedRow[];
        },
        enabled: activeTab === 'allowlist',
    });

    const { data: allowlistSearchResults = [], isLoading: allowlistSearchLoading } = useQuery({
        queryKey: ['admin', 'search-users-ai-video-allow', allowlistUserSearch],
        queryFn: async () => {
            if (!allowlistUserSearch || allowlistUserSearch.trim().length < 2) return [];
            const { data, error } = await dbClient.rpc<AllowlistUserSearch[]>('admin_search_users', {
                p_search_query: allowlistUserSearch.trim(),
                p_search_by_email: true,
                p_result_limit: 10,
            });
            if (error) throw error;
            return (normalizeRpcResponse(data) as AllowlistUserSearch[]).map(u => ({
                user_id: u.user_id,
                display_name: u.display_name ?? null,
                email: u.email ?? null,
            }));
        },
        enabled: allowlistUserSearch.trim().length >= 2 && activeTab === 'allowlist' && showAllowlistSearch,
        staleTime: 30_000,
    });

    const addUnlimitedMutation = useMutation({
        mutationFn: async (userId: string) => {
            const { error } = await dbClient.rpc('admin_add_ai_video_unlimited', { p_user_id: userId });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'ai-video-unlimited'] });
            setAllowlistUserSearch('');
            setShowAllowlistSearch(false);
        },
    });

    const removeUnlimitedMutation = useMutation({
        mutationFn: async (userId: string) => {
            const { error } = await dbClient.rpc('admin_remove_ai_video_unlimited', { p_user_id: userId });
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'ai-video-unlimited'] }),
    });

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (allowlistSearchRef.current && !allowlistSearchRef.current.contains(e.target as Node)) {
                setShowAllowlistSearch(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // ─── Models ────────────────────────────────
    const { data: models = [], isLoading: modelsLoading, refetch: refetchModels } = useQuery({
        queryKey: ['admin', 'ai-video-models'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<VideoModel[]>('get_all_video_models_admin');
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    // ─── Prompts ───────────────────────────────
    const { data: prompts = [], isLoading: promptsLoading, refetch: refetchPrompts } = useQuery({
        queryKey: ['admin', 'ai-video-prompts'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<VideoPrompt[]>('get_all_video_prompts_admin');
            if (error) throw error;
            return normalizeRpcResponse(data);
        },
    });

    // ─── Model Mutations ─────────────────────────────
    const modelUpdateMutation = useMutation({
        mutationFn: async (params: { modelId: string; tokensCost?: number; isActive?: boolean; isDefault?: boolean }) => {
            const { error } = await dbClient.rpc('update_video_model', {
                p_model_id: params.modelId,
                p_tokens_cost: params.tokensCost,
                p_is_active: params.isActive,
                p_is_default: params.isDefault,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'ai-video-models'] });
            setEditedTokens({});
        },
    });

    const tierUpdateMutation = useMutation({
        mutationFn: async (params: { modelId: string; tierLevel: number; isEnabled: boolean }) => {
            const { error } = await dbClient.rpc('update_video_model_tier', {
                p_model_id: params.modelId,
                p_tier_level: params.tierLevel,
                p_is_enabled: params.isEnabled,
            });
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'ai-video-models'] }),
    });

    // ─── Prompt Mutations ─────────────────────────────
    const promptToggleMutation = useMutation({
        mutationFn: async (params: { promptId: string; isActive: boolean }) => {
            const { error } = await dbClient.rpc('admin_update_video_prompt', {
                p_prompt_id: params.promptId,
                p_is_active: params.isActive,
            });
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'ai-video-prompts'] }),
    });

    const promptSetDefaultMutation = useMutation({
        mutationFn: async (params: { promptId: string }) => {
            const { error } = await dbClient.rpc('update_video_prompt', {
                p_prompt_id: params.promptId,
                p_is_default: true,
            });
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'ai-video-prompts'] }),
    });

    const promptCreateMutation = useMutation({
        mutationFn: async (data: PromptFormData) => {
            const { error } = await dbClient.rpc('create_video_prompt', {
                p_name: data.name,
                p_slug: data.slug || generateSlug(data.name),
                p_category: data.category,
                p_prompt_template: data.promptTemplate,
                p_description: data.description || null,
                p_icon: data.icon,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'ai-video-prompts'] });
            setIsDialogOpen(false);
            setFormData(EMPTY_FORM);
            setEditingPrompt(null);
        },
    });

    const promptUpdateMutation = useMutation({
        mutationFn: async (params: { promptId: string; data: PromptFormData }) => {
            const { error } = await dbClient.rpc('update_video_prompt', {
                p_prompt_id: params.promptId,
                p_name: params.data.name,
                p_slug: params.data.slug,
                p_category: params.data.category,
                p_prompt_template: params.data.promptTemplate,
                p_description: params.data.description || null,
                p_icon: params.data.icon,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'ai-video-prompts'] });
            setIsDialogOpen(false);
            setFormData(EMPTY_FORM);
            setEditingPrompt(null);
        },
    });

    // ─── Dialog Handlers ─────────────────────────────
    const openCreateDialog = () => {
        setEditingPrompt(null);
        setFormData(EMPTY_FORM);
        setIsDialogOpen(true);
    };

    const openEditDialog = (prompt: VideoPrompt) => {
        setEditingPrompt(prompt);
        setFormData({
            name: prompt.name,
            slug: prompt.slug,
            category: prompt.category,
            promptTemplate: prompt.prompt_template,
            description: prompt.description || '',
            icon: prompt.icon || 'waves',
        });
        setIsDialogOpen(true);
    };

    const handleSavePrompt = () => {
        if (editingPrompt) {
            promptUpdateMutation.mutate({ promptId: editingPrompt.id, data: formData });
        } else {
            promptCreateMutation.mutate(formData);
        }
    };

    const isSavingPrompt = promptCreateMutation.isPending || promptUpdateMutation.isPending;

    const handleRefresh = () => {
        if (activeTab === 'models') refetchModels();
        else if (activeTab === 'prompts') refetchPrompts();
        else refetchUnlimited();
    };

    const TABS = [
        { value: 'models', label: 'Modelos', icon: <Video size={14} /> },
        { value: 'prompts', label: 'Estilos / Prompts', icon: <Zap size={14} /> },
        { value: 'allowlist', label: 'Cuota ilimitada', icon: <Users size={14} /> },
    ] as const;

    const filteredAllowlistSearch = allowlistSearchResults.filter(
        u => !unlimitedRows.some(r => r.user_id === u.user_id)
    );

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <PageHeader
                    title="Video IA"
                    description={
                        activeTab === 'allowlist'
                            ? 'Usuarios exentos del límite mensual de generación (además de modelos y estilos).'
                            : 'Modelos por nivel (tier), coste de referencia interno y prompts de animación'
                    }
                />
                <button
                    onClick={handleRefresh}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                    <RefreshCw size={14} />
                    Recargar
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit">
                {TABS.map(t => (
                    <button
                        key={t.value}
                        onClick={() => setActiveTab(t.value)}
                        className={cn(
                            'flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                            activeTab === t.value
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {t.icon}
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ─── Allowlist Tab ───────────────────────────────────────── */}
            {activeTab === 'allowlist' && (
                <div className="space-y-4 max-w-3xl">
                    <p className="text-sm text-muted-foreground">
                        Los usuarios listados quedan exentos del tope de seguridad Pro (20 vídeos animados/mes en código de generación). La app usa la RPC{' '}
                        <code className="text-[11px]">get_my_ai_video_unlimited</code>; Azure Functions usa{' '}
                        <code className="text-[11px]">check_ai_video_unlimited_by_user_id</code> (misma lista en base de datos).
                    </p>
                    <div className="relative" ref={allowlistSearchRef}>
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Buscar por email o nombre para añadir…"
                            value={allowlistUserSearch}
                            onChange={e => {
                                setAllowlistUserSearch(e.target.value);
                                setShowAllowlistSearch(true);
                            }}
                            onFocus={() => setShowAllowlistSearch(true)}
                            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        {showAllowlistSearch && allowlistUserSearch.trim().length >= 2 && (
                            <div className="absolute z-20 mt-1 w-full rounded-lg border border-border bg-card shadow-lg max-h-56 overflow-y-auto">
                                {allowlistSearchLoading ? (
                                    <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                                        <Loader2 size={14} className="animate-spin" /> Buscando…
                                    </div>
                                ) : filteredAllowlistSearch.length === 0 ? (
                                    <div className="p-3 text-sm text-muted-foreground">Sin resultados</div>
                                ) : (
                                    filteredAllowlistSearch.map(u => (
                                        <button
                                            key={u.user_id}
                                            type="button"
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex justify-between items-start gap-2"
                                            onClick={() => addUnlimitedMutation.mutate(u.user_id)}
                                            disabled={addUnlimitedMutation.isPending}
                                        >
                                            <span>
                                                <span className="font-medium text-foreground">{u.display_name || 'Sin nombre'}</span>
                                                <span className="block text-xs text-muted-foreground">{u.email}</span>
                                            </span>
                                            <Plus size={14} className="text-primary shrink-0 mt-0.5" />
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>

                    <div className="rounded-xl border border-border bg-card overflow-hidden">
                        <div className="px-4 py-2 border-b border-border bg-muted/30 text-xs font-semibold text-muted-foreground">
                            Usuarios con generación ilimitada ({unlimitedRows.length})
                        </div>
                        {unlimitedLoading ? (
                            <div className="py-12 flex justify-center">
                                <Loader2 size={22} className="animate-spin text-muted-foreground" />
                            </div>
                        ) : unlimitedRows.length === 0 ? (
                            <div className="py-8 text-center text-sm text-muted-foreground">Nadie en la lista todavía.</div>
                        ) : (
                            <ul className="divide-y divide-border">
                                {unlimitedRows.map(row => (
                                    <li key={row.user_id} className="flex items-center justify-between gap-3 px-4 py-3">
                                        <div className="min-w-0">
                                            <p className="font-medium text-sm text-foreground truncate">
                                                {row.display_name || 'Sin nombre'}
                                            </p>
                                            <p className="text-xs text-muted-foreground truncate">{row.email}</p>
                                        </div>
                                        <button
                                            type="button"
                                            className="p-2 rounded-lg hover:bg-destructive/10 text-destructive shrink-0"
                                            onClick={() => removeUnlimitedMutation.mutate(row.user_id)}
                                            disabled={removeUnlimitedMutation.isPending}
                                            title="Quitar"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {/* ─── Models Tab ──────────────────────────────────────────── */}
            {activeTab === 'models' && (
                <div className="space-y-4">
                    {/* Quality Tier Legend */}
                    <div className="flex flex-wrap gap-3 text-xs">
                        {Object.entries(TIER_LABELS).map(([key, label]) => {
                            const c = TIER_COLORS[key];
                            return (
                                <span key={key} className={cn('px-2 py-1 rounded-md font-medium', c.bg, c.text)}>{label}</span>
                            );
                        })}
                    </div>

                    {/* Models Table */}
                    <div className="rounded-xl border border-border bg-card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border bg-muted/30">
                                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Modelo</th>
                                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground">Calidad</th>
                                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground">
                                            <span className="flex items-center justify-center gap-1"><Clock size={12} /> Tiempo</span>
                                        </th>
                                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground">
                                            <span className="flex items-center justify-center gap-1"><Coins size={12} /> Tokens</span>
                                        </th>
                                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground">Free</th>
                                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground">Pro</th>
                                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground">Premium</th>
                                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground">Activo</th>
                                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground">Default</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {modelsLoading ? (
                                        <tr><td colSpan={9} className="py-12 text-center">
                                            <RefreshCw size={18} className="animate-spin mx-auto text-muted-foreground" />
                                        </td></tr>
                                    ) : models.map(model => {
                                        const tc = TIER_COLORS[model.quality_tier] || TIER_COLORS.standard;
                                        const hasEdited = editedTokens[model.id] !== undefined;

                                        return (
                                            <tr key={model.id} className={cn('hover:bg-muted/20 transition-colors', !model.is_active && 'opacity-50')}>
                                                <td className="px-4 py-2.5">
                                                    <p className="font-medium text-foreground text-xs">{model.name}</p>
                                                    <p className="text-[10px] text-muted-foreground font-mono">{model.slug}</p>
                                                </td>
                                                <td className="px-3 py-2.5 text-center">
                                                    <span className={cn('px-2 py-0.5 rounded text-[10px] font-medium', tc.bg, tc.text)}>
                                                        {TIER_LABELS[model.quality_tier] || model.quality_tier}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2.5 text-center text-xs text-muted-foreground">
                                                    {model.generation_time_avg_seconds ? `~${model.generation_time_avg_seconds}s` : '-'}
                                                </td>
                                                <td className="px-3 py-2.5 text-center">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <input
                                                            type="number"
                                                            className="w-14 px-1 py-1 rounded border border-border bg-background text-foreground text-xs text-center focus:ring-1 focus:ring-primary outline-none"
                                                            value={editedTokens[model.id] ?? model.tokens_cost}
                                                            onChange={e => setEditedTokens(p => ({ ...p, [model.id]: parseInt(e.target.value) || 0 }))}
                                                            min={1}
                                                        />
                                                        {hasEdited && (
                                                            <button
                                                                onClick={() => modelUpdateMutation.mutate({ modelId: model.id, tokensCost: editedTokens[model.id] })}
                                                                className="p-1 rounded hover:bg-muted transition-colors"
                                                                disabled={modelUpdateMutation.isPending}
                                                            >
                                                                <Save size={12} className="text-primary" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                {/* Tier checkboxes */}
                                                {PLAN_BADGES.map((tier, idx) => (
                                                    <td key={tier.key} className="px-3 py-2.5 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={(model as any)[tier.key]}
                                                            onChange={e => tierUpdateMutation.mutate({
                                                                modelId: model.id, tierLevel: idx, isEnabled: e.target.checked
                                                            })}
                                                            className="rounded border-border w-3.5 h-3.5 accent-primary"
                                                        />
                                                    </td>
                                                ))}
                                                <td className="px-3 py-2.5 text-center">
                                                    <button
                                                        onClick={() => modelUpdateMutation.mutate({ modelId: model.id, isActive: !model.is_active })}
                                                        className={cn(
                                                            'w-8 h-4 rounded-full relative transition-colors',
                                                            model.is_active ? 'bg-emerald-500' : 'bg-zinc-600'
                                                        )}
                                                    >
                                                        <span className={cn(
                                                            'absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all',
                                                            model.is_active ? 'left-4' : 'left-0.5'
                                                        )} />
                                                    </button>
                                                </td>
                                                <td className="px-3 py-2.5 text-center">
                                                    <button
                                                        onClick={() => !model.is_default && modelUpdateMutation.mutate({ modelId: model.id, isDefault: true })}
                                                        disabled={model.is_default}
                                                        className="p-1"
                                                    >
                                                        {model.is_default
                                                            ? <CheckCircle size={14} className="text-primary" />
                                                            : <div className="w-3.5 h-3.5 rounded-full border-2 border-muted-foreground" />
                                                        }
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Cost Summary */}
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { label: 'Basic (tier_basic)', filter: (m: VideoModel) => m.tier_basic && m.is_active, headerColor: '' },
                            { label: 'Pro (tier_pro)', filter: (m: VideoModel) => m.tier_pro && m.is_active, headerColor: 'text-blue-400' },
                            { label: 'Premium (tier_premium)', filter: (m: VideoModel) => m.tier_premium && m.is_active, headerColor: 'text-purple-400' },
                        ].map(({ label, filter, headerColor }) => {
                            const available = models.filter(filter);
                            return (
                                <div key={label} className="rounded-xl border border-border bg-card p-4">
                                    <p className={cn("text-xs font-semibold mb-2", headerColor || 'text-foreground')}>{label}</p>
                                    {available.length > 0 ? available.map(m => (
                                        <div key={m.id} className="flex justify-between text-xs py-0.5">
                                            <span className="text-muted-foreground">{m.name}</span>
                                            <span className="text-foreground">{m.tokens_cost} (ref.)</span>
                                        </div>
                                    )) : (
                                        <p className="text-xs text-muted-foreground italic">Sin modelos</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Informational Note */}
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                                <p className="font-medium text-amber-500">Nota sobre costes</p>
                                <p className="text-muted-foreground mt-1">
                                    El número por modelo es una referencia interna (comparar modelos entre sí); no es el saldo
                                    que descuenta la app al usuario por vídeo (la app usa tope Pro/premios). Para coste real
                                    del proveedor, revisa Fal.ai.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Prompts Tab ─────────────────────────────────────────── */}
            {activeTab === 'prompts' && (
                <div className="space-y-4">
                    {/* Header with Create button */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-base font-semibold flex items-center gap-2 text-foreground">
                                <Wand2 size={16} className="text-purple-400" />
                                Estilos de Animación
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Gestiona los prompts que controlan cómo se generan las animaciones
                            </p>
                        </div>
                        <button
                            onClick={openCreateDialog}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                        >
                            <Plus size={14} />
                            Nuevo Estilo
                        </button>
                    </div>

                    {/* Prompts Grid (Card view like the app) */}
                    {promptsLoading ? (
                        <div className="flex justify-center py-12">
                            <RefreshCw size={18} className="animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {prompts.map(prompt => {
                                const IconComp = getIconComponent(prompt.icon || 'wand2');
                                const catColor = CATEGORY_COLORS[prompt.category] || CATEGORY_COLORS.general;

                                return (
                                    <div
                                        key={prompt.id}
                                        className={cn(
                                            'rounded-xl border border-border bg-card p-4 space-y-3 transition-opacity',
                                            !prompt.is_active && 'opacity-50'
                                        )}
                                    >
                                        {/* Card Header */}
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                                                    <IconComp size={14} className="text-primary" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-sm font-semibold text-foreground truncate">{prompt.name}</p>
                                                        {prompt.is_default && (
                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/15 text-primary shrink-0">Default</span>
                                                        )}
                                                    </div>
                                                    <p className="text-[10px] text-muted-foreground font-mono">{prompt.slug}</p>
                                                </div>
                                            </div>
                                            <span className={cn('px-2 py-0.5 rounded text-[10px] font-medium shrink-0', catColor)}>
                                                {CATEGORY_LABELS[prompt.category] || prompt.category}
                                            </span>
                                        </div>

                                        {/* Description */}
                                        <p className="text-xs text-muted-foreground line-clamp-2">
                                            {prompt.description || 'Sin descripción'}
                                        </p>

                                        {/* Prompt Preview */}
                                        <div className="p-2 rounded-lg bg-muted/30 text-[10px] font-mono text-muted-foreground line-clamp-3">
                                            {prompt.prompt_template || 'Sin prompt definido'}
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center justify-between pt-2 border-t border-border/50">
                                            <div className="flex items-center gap-2">
                                                {/* Active toggle */}
                                                <button
                                                    onClick={() => promptToggleMutation.mutate({ promptId: prompt.id, isActive: !prompt.is_active })}
                                                    className={cn(
                                                        'w-8 h-4 rounded-full relative transition-colors',
                                                        prompt.is_active ? 'bg-emerald-500' : 'bg-zinc-600'
                                                    )}
                                                >
                                                    <span className={cn(
                                                        'absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all',
                                                        prompt.is_active ? 'left-4' : 'left-0.5'
                                                    )} />
                                                </button>
                                                <span className="text-[10px] text-muted-foreground">
                                                    {prompt.is_active ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {/* Set Default */}
                                                {!prompt.is_default && (
                                                    <button
                                                        onClick={() => promptSetDefaultMutation.mutate({ promptId: prompt.id })}
                                                        className="p-1.5 rounded-md hover:bg-muted transition-colors"
                                                        title="Marcar como default"
                                                    >
                                                        <CheckCircle size={13} className="text-muted-foreground" />
                                                    </button>
                                                )}
                                                {/* Edit */}
                                                <button
                                                    onClick={() => openEditDialog(prompt)}
                                                    className="p-1.5 rounded-md hover:bg-muted transition-colors"
                                                    title="Editar estilo"
                                                >
                                                    <Pencil size={13} className="text-muted-foreground" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ─── Create/Edit Prompt Dialog ──────────────────────────── */}
            {isDialogOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={() => { setIsDialogOpen(false); setEditingPrompt(null); setFormData(EMPTY_FORM); }}
                    />

                    {/* Dialog */}
                    <div className="relative w-full max-w-xl mx-4 rounded-xl border border-border bg-card shadow-2xl">
                        {/* Header */}
                        <div className="flex items-center justify-between p-5 border-b border-border">
                            <div>
                                <h3 className="text-base font-semibold text-foreground">
                                    {editingPrompt ? 'Editar Estilo' : 'Nuevo Estilo de Animación'}
                                </h3>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {editingPrompt
                                        ? 'Modifica el prompt y la configuración del estilo'
                                        : 'Crea un nuevo estilo de animación con su prompt personalizado'}
                                </p>
                            </div>
                            <button
                                onClick={() => { setIsDialogOpen(false); setEditingPrompt(null); setFormData(EMPTY_FORM); }}
                                className="p-1.5 rounded-md hover:bg-muted transition-colors"
                            >
                                <X size={16} className="text-muted-foreground" />
                            </button>
                        </div>

                        {/* Form */}
                        <div className="p-5 space-y-4">
                            {/* Name & Slug */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-foreground">Nombre</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={e => setFormData(prev => ({
                                            ...prev,
                                            name: e.target.value,
                                            slug: editingPrompt ? prev.slug : generateSlug(e.target.value),
                                        }))}
                                        placeholder="Ej: Aguas Turbulentas"
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:ring-1 focus:ring-primary outline-none"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-foreground">Slug</label>
                                    <input
                                        type="text"
                                        value={formData.slug}
                                        onChange={e => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                                        placeholder="aguas-turbulentas"
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm font-mono focus:ring-1 focus:ring-primary outline-none"
                                    />
                                </div>
                            </div>

                            {/* Category & Icon */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-foreground">Categoría</label>
                                    <select
                                        value={formData.category}
                                        onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:ring-1 focus:ring-primary outline-none"
                                    >
                                        <option value="subtle">Sutil</option>
                                        <option value="dramatic">Dramático</option>
                                        <option value="artistic">Artístico</option>
                                        <option value="general">General</option>
                                    </select>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-foreground">Icono</label>
                                    <select
                                        value={formData.icon}
                                        onChange={e => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:ring-1 focus:ring-primary outline-none"
                                    >
                                        {AVAILABLE_ICONS.map(({ value, label }) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-foreground">Descripción</label>
                                <input
                                    type="text"
                                    value={formData.description}
                                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Breve descripción del estilo para el usuario"
                                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:ring-1 focus:ring-primary outline-none"
                                />
                            </div>

                            {/* Prompt Template */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-foreground">Prompt de Animación</label>
                                <textarea
                                    value={formData.promptTemplate}
                                    onChange={e => setFormData(prev => ({ ...prev, promptTemplate: e.target.value }))}
                                    placeholder="Describe cómo debe animarse la imagen. Ej: Animate this fishing photo with subtle, natural motion..."
                                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm font-mono focus:ring-1 focus:ring-primary outline-none min-h-[120px] resize-y"
                                />
                                <p className="text-[10px] text-muted-foreground">
                                    Este texto se enviará al modelo de IA para generar la animación
                                </p>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-end gap-2 p-5 border-t border-border">
                            <button
                                onClick={() => { setIsDialogOpen(false); setEditingPrompt(null); setFormData(EMPTY_FORM); }}
                                className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSavePrompt}
                                disabled={isSavingPrompt || !formData.name || !formData.promptTemplate}
                                className={cn(
                                    'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                                    'bg-primary text-primary-foreground hover:bg-primary/90',
                                    (isSavingPrompt || !formData.name || !formData.promptTemplate) && 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                {isSavingPrompt ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <Save size={14} />
                                )}
                                {editingPrompt ? 'Guardar Cambios' : 'Crear Estilo'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
